import { Fragment, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { observer } from 'decorators';
import { useDisposable } from 'hooks';
import { installScript } from 'utils/dom';
import config from 'config';

let loadPromise = null;
function loadRecaptcha() {
  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      // Predefine callback
      window.recaptcha_callback = () => {
        window.recaptcha_callback = null;
        resolve(window.grecaptcha);
      };

      installScript('https://www.google.com/recaptcha/api.js?onload=recaptcha_callback&render=explicit')
        .catch(err => {
          loadPromise = null;
          reject(err);
        })
        .done();
    });
  }
  return loadPromise;
}

class RecaptchaStore {
  dispose() {
    this._mounted = false;
    if (this._gc) {
      try { this._gc.reset(this._renderId); } catch {}
    }
    this._gc = null;
    this._renderId = null;
    if (this._reject) {
      this._reject(new Error('Recaptcha canceled'));
    }
    this._reject = null;
    this._resolve = null;
    if (this._removeListener) {
      this._removeListener();
      this._removeListener = null;
    }
  }

  init(div) {
    this._mounted = true;
    this._initPromise = loadRecaptcha()
      .then(gc => {
        if (!this._mounted) { return; }
        this._gc = gc;
        this._renderId = gc.render(div, {
          sitekey: config.google.recaptcha,
          callback: t => this._resolve && this._resolve(t),
          'error-callback': () => this._onError('Recaptcha failed'),
          'expire-callback': () => this._onError('Recaptcha expired'),
          size: 'invisible',
          badge: 'bottomleft'
        });
        return null;
      });

    this._initPromise.done();
  }

  execute() {
    return this._initPromise.then(() =>
      new Promise((resolve, reject) => {
        this._resolve = resolve;
        this._reject = reject;

        // Hack to handle window being closed...
        try {
          /* find challenge iframe */
          const recaptchaIframe = [ ...document.body.getElementsByTagName('iframe') ].find(el => el.src.match('https://www.google.com/recaptcha/api2/bframe'));
          const recaptchaOverlay = recaptchaIframe.parentElement.parentElement.firstElementChild;

          /* detect when the recaptcha challenge window is closed and reset captcha */
          const onCancel = () => this._onError('Recaptcha canceled');
          recaptchaOverlay.addEventListener('click', onCancel);
          /* save remove event listener for click event */
          this._removeListener = () => recaptchaOverlay.removeEventListener('click', onCancel);
        } catch (error) {
          console.error(error);
        }

        this._gc.execute(this._renderId);
      })
    )
      .finally(() => {
        if (this._removeListener) {
          this._removeListener();
          this._removeListener = null;
        }
        if (this._gc) {
          try { this._gc.reset(this._renderId); } catch {}
        }
        this._reject = null;
        this._resolve = null;
      });
  }

  _onError(errMessage) {
    if (this._reject) {
      this._reject(new Error(errMessage));
    }
    try { this._gc.reset(this._renderId); } catch {}
  }
}

export default observer(forwardRef(function Recaptcha({ render }, ref) {
  const divRef = useRef(null);
  const store = useDisposable(() => new RecaptchaStore());

  const execute = useCallback(() => store.execute(), [ store ]);

  useEffect(() => store.init(divRef.current), [ store ]);

  // Can manually call execute based on ref
  useImperativeHandle(ref, () => ({ execute }), [ execute ]);

  return <Fragment>
    <div ref={divRef} style={{ position: 'fixed', zIndex: 100000 }} />
    { render && render({ execute }) }
  </Fragment>;
}));